// Card and bank status on Settings, Cards and banks: always derived from what Hush observed,
// never stored or ticked by hand. Every rule lives here, with tests in test/card-status.test.ts.
// Pure: no I/O, no clock (takes `now`).
//
//   Not connected      The card's bank is not on the household's list (no allowed alert address).
//   Waiting            Its bank is on the list, but no alert for this card has come through the
//                      Gmail filter yet. A hand-forwarded example or a pasted alert never counts.
//   Working            An alert for this card has come through the Gmail filter.
//   Needs attention    It worked, then went quiet for far longer than usual (the bank's silence
//                      warning, or the card itself quiet for CARD_STATUS.QUIET_MIN_DAYS and 3x its
//                      usual gap); or it has been waiting longer than CARD_STATUS.WAITING_MAX_DAYS
//                      since it was added (the Gmail step most likely did not finish).

export type CardStatus = 'not_connected' | 'waiting' | 'working' | 'attention';

export const CARD_STATUS = {
  /** A working card quiet this long (and 3x its usual gap) needs attention. */
  QUIET_MIN_DAYS: 30,
  QUIET_GAP_MULTIPLIER: 3,
  /** Waiting this long after it was added: something in the setup did not finish. */
  WAITING_MAX_DAYS: 14,
} as const;

const DAY = 86_400_000;

export interface CardStatusInput {
  /** Its bank is on the household's list (an allowed alert address). */
  connected: boolean;
  /** When the card was added (by the flow, or by its first alert). */
  addedAt: string;
  /** Alerts for this card that came through the Gmail filter, any order (ISO times). */
  filterAlerts: string[];
  /** The card's bank has an open silence warning (src/intake/silence.ts). */
  bankSilent: boolean;
}

export interface CardStatusResult {
  status: CardStatus;
  /** Why it needs attention, for the details line. */
  reason: 'quiet' | 'bank-quiet' | 'never-arrived' | null;
  lastAlertAt: string | null;
}

function median(xs: number[]): number {
  const s = [...xs].sort((a, b) => a - b);
  const m = s.length >> 1;
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

/** Days of quiet after which a working card needs attention: max(30, 3 x its median gap). */
export function cardQuietDays(times: number[]): number {
  const sorted = [...times].sort((a, b) => a - b);
  const gaps = sorted.slice(1).map((t, i) => (t - sorted[i]) / DAY);
  return Math.max(CARD_STATUS.QUIET_MIN_DAYS, gaps.length ? CARD_STATUS.QUIET_GAP_MULTIPLIER * median(gaps) : 0);
}

export function cardStatus(c: CardStatusInput, now: Date): CardStatusResult {
  const times = c.filterAlerts.map((t) => Date.parse(t)).filter((t) => !Number.isNaN(t)).sort((a, b) => a - b);
  const last = times.length ? times[times.length - 1] : null;
  const lastAlertAt = last === null ? null : new Date(last).toISOString();
  if (!c.connected) return { status: 'not_connected', reason: null, lastAlertAt };
  if (last === null) {
    const waited = (now.getTime() - Date.parse(c.addedAt)) / DAY;
    return waited > CARD_STATUS.WAITING_MAX_DAYS ? { status: 'attention', reason: 'never-arrived', lastAlertAt } : { status: 'waiting', reason: null, lastAlertAt };
  }
  if (c.bankSilent) return { status: 'attention', reason: 'bank-quiet', lastAlertAt };
  if ((now.getTime() - last) / DAY > cardQuietDays(times)) return { status: 'attention', reason: 'quiet', lastAlertAt };
  return { status: 'working', reason: null, lastAlertAt };
}

/** A bank's status: the worst of its cards; with no cards, from its own filter alerts. */
export function bankStatus(cards: CardStatus[], own: CardStatusResult): CardStatus {
  const all = [...cards, ...(cards.length ? [] : [own.status])];
  for (const s of ['attention', 'not_connected', 'waiting', 'working'] as const) if (all.includes(s)) return s;
  return own.status;
}

export const STATUS_LABEL: Record<CardStatus, string> = {
  not_connected: 'Not connected',
  waiting: 'Waiting for first alert',
  working: 'Working',
  attention: 'Needs attention',
};

/** The short chip text. */
export const STATUS_CHIP: Record<CardStatus, string> = {
  not_connected: 'Not connected',
  waiting: 'Waiting',
  working: 'Working',
  attention: 'Needs attention',
};

/** One line under a card that needs attention. */
export const ATTENTION_REASON: Record<NonNullable<CardStatusResult['reason']>, string> = {
  quiet: 'No alert for a long time. Check the bank still emails you for this card.',
  'bank-quiet': 'No alerts from this bank lately. Check your Gmail filter.',
  'never-arrived': 'No alert has come through yet. The Gmail step may not have finished.',
};

/** The Settings menu status: "3 working, 1 waiting". Empty counts are left out. */
export function statusSummary(statuses: CardStatus[]): string {
  if (!statuses.length) return 'Not set up';
  const n = (s: CardStatus) => statuses.filter((x) => x === s).length;
  const parts = [
    n('working') && `${n('working')} working`,
    n('waiting') && `${n('waiting')} waiting`,
    n('attention') && `${n('attention')} need${n('attention') === 1 ? 's' : ''} attention`,
    n('not_connected') && `${n('not_connected')} not connected`,
  ].filter(Boolean);
  return parts.join(', ');
}
